import React, { useState } from 'react';
import MUIAutocomplete from '@mui/material/Autocomplete';
import TextField from '@mui/material/TextField';
import Chip from '@mui/material/Chip';
import styled from 'styled-components';
import { Controller, useFormContext } from 'react-hook-form';
import { useTranslation } from '../../../hooks/useTranslation';

interface AutocompleteInputProps {
  errorMessage?: string;
  disabled?: boolean;
  fullWidth?: boolean;
  initialValue?: string[];
  label?: string;
  maxItemLength?: number;
  maxItems?: number;
  name: string;
  options: string[];
  placeholder?: string;
  required?: boolean;
  customOptionEnabled?: boolean;
  getOptionLabel?: (option: string) => string;
  autoSelect?: boolean;
}

const StyledChip = styled(Chip)`
  background-color: #e0e0e0 !important; /* Grey background */
  border-radius: 10px !important; /* Rounded corners */
  margin: 2px !important; /* Margin between chips */
`;

const AutocompleteInput: React.FC<AutocompleteInputProps> = ({
  errorMessage,
  disabled = false,
  fullWidth = false,
  initialValue = [],
  label,
  maxItemLength,
  maxItems,
  name,
  options,
  placeholder = '',
  required = false,
  customOptionEnabled = true,
  getOptionLabel,
  autoSelect = false,
  ...props
}) => {
  const { control } = useFormContext();
  const { t } = useTranslation();
  const [localOptions, setLocalOptions] = useState<string[]>(options);

  return (
    <Controller
      name={name}
      control={control}
      defaultValue={initialValue}
      rules={{
        required: required
          ? (errorMessage ?? t('This field is required'))
          : false,
      }}
      render={({ field, fieldState }) => (
        <MUIAutocomplete
          multiple
          options={localOptions}
          value={field.value || []}
          onChange={(
            event: React.SyntheticEvent<Element, Event>,
            newValue: string[]
          ) => {
            const cappedValue = maxItems
              ? newValue.slice(0, maxItems)
              : newValue;
            const trimmedValue = maxItemLength
              ? cappedValue.map(val => val.slice(0, maxItemLength))
              : cappedValue;
            // Make sure that user provided option is does not exists yet from
            // available options
            const newOptions = trimmedValue.filter(
              val => !localOptions.includes(val)
            );
            if (newOptions.length > 0) {
              setLocalOptions(prevOptions => [...prevOptions, ...newOptions]);
            }
            field.onChange(trimmedValue);
          }}
          disabled={disabled}
          fullWidth={fullWidth}
          freeSolo={customOptionEnabled}
          autoSelect={autoSelect}
          // Picking a chosen option again on blur would remove it.
          filterSelectedOptions={autoSelect}
          getOptionLabel={getOptionLabel}
          renderInput={params => (
            <TextField
              {...params}
              label={label}
              placeholder={placeholder}
              error={!!fieldState.error}
              helperText={fieldState.error?.message}
              inputProps={{ ...params.inputProps, maxLength: maxItemLength }}
            />
          )}
          renderTags={(selectedOptions, getTagProps) =>
            selectedOptions.map((option: string, index: number) => {
              const { key, ...tagProps } = getTagProps({ index });
              return (
                <StyledChip
                  key={key}
                  label={getOptionLabel ? getOptionLabel(option) : option}
                  {...tagProps}
                />
              );
            })
          }
          {...props}
        />
      )}
    />
  );
};

export default AutocompleteInput;
